import { z } from 'zod';
import { emailAddressField } from './email-address.js';
import { MEMBER_OPTIONAL_MATCHING_VOCABULARIES, MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK, } from './member-matching-fields.js';
import { EDGE_WHITESPACE, PASSWORD_EDGE_SPACE_MESSAGE, PASSWORD_POLICY, describePasswordViolation, } from './password-policy.js';
/**
 * Member registration — the wizard's shape, its field rules, and its copy (`MEM-001`…`MEM-004`).
 *
 * ## Why this lives in `contracts` rather than in the API or the SPA
 *
 * Three things need the same answers and must not disagree about them: the wizard renders a step's
 * fields and validates them per step (`MEM-001` AC-2), the API enforces the same rules on the way
 * in, and the tests assert them. `apps/web` has no DOM test runner, so logic left in a component is
 * logic nobody can hold; put here it is shared *and* covered.
 *
 * ## Five steps, because the client chose the prototype (2026-08-21)
 *
 * This module was four steps until 2026-08-21. The stories specified four; `Member App.dc.html`'s
 * `showRegister` block has five; the disagreement was raised on the M1.1 pull request rather than
 * resolved in code, and **the client answered: use the prototype.**
 * `docs/plans/stage-m1-member-registration.md`, "Held for the client" item 1, records it.
 *
 * So the steps below are `rs0`…`rs4` as the markup groups them, and the indicator reads `n/5`
 * because `regLabel` does.
 *
 * **The markup's grouping is not the obvious one and is easy to transcribe wrongly.** `rs2` holds
 * *two* headings — "What are you working toward?" and "What kind of connection are you seeking?" —
 * on one step, while formats and the privacy elections, which read like one thought, are `rs3` and
 * `rs4` separately. A reading that splits goal from intent and folds privacy into the format step
 * also produces five steps, and is wrong. `regStep` is the arbiter:
 *
 * ```
 * rs0  First, the basics                    name · role · company · email · password · Google
 * rs1  Pick 3–5 interests                   chips, with a live "N selected" count
 * rs2  What are you working toward?         current goal  +  connection intent ("Pick all that apply")
 * rs3  How do you like to connect?          format (single-select) + optional LinkedIn
 * rs4  You're in control                    the three privacy elections
 * ```
 *
 * ## What the prototype stopped collecting, and what came back
 *
 * The four-step wizard collected five fields the prototype never asks for: **conversation starter,
 * city, industry, career stage and social comfort.** Following the prototype removed all five on
 * 2026-08-21, and three of them were load-bearing: industry and career stage feed
 * `platform-config`'s `proximity` weight (**16 of 100**) and social comfort feeds `socialComfort`
 * (**10 of 100**). The measurement that followed put a number on it — a realistic room of twelve
 * scored 65 pairs against a threshold of 62, with a reachable ceiling of 74.
 *
 * **On 2026-08-22 the client put four of the five back, as optional fields on the existing five
 * steps** (`MEM-045`). Their placement, their copy and the rule that an empty answer is excluded
 * rather than scored zero all live in {@link ./member-matching-fields.js}; this module's schemas
 * accept them and nothing here treats them as required. **City stays withdrawn** — it fed no
 * weight and no feature, so it was the one removal with no cost to reverse.
 *
 * That the reversal is cheap is not luck. The attributes were kept on the row rather than deleted
 * and the vocabularies were kept authored and seeded, precisely so that re-collecting would be a
 * form control rather than a migration. It was.
 *
 * ## Saving is explicit
 *
 * There is no autosave here, for the same reason there is no longer one on the event wizard: the
 * client removed it on 2026-08-19 (`EVT-002` AC-3) because it wrote to the database without a user
 * action. **Continue is the save.** It is a thing the member did, so it writes; nothing else does.
 * Field change, blur, and the passage of time write nothing, and there is no timer anywhere in this
 * flow.
 *
 * That is also what makes `MEM-001` AC-5's "resumes where they stopped, **on any device**" work
 * without a cache: because the account exists from step 1 (`MEM-002` AC-2) and each Continue
 * commits that step's answers server-side, the resume point is a stored fact rather than something
 * recovered from the tab that was closed. See {@link resumeStepFor}.
 */
/* ============================================================================
   Steps
   ============================================================================ */
export const MEMBER_REGISTRATION_STEP_IDS = [
    'basics',
    'interests',
    'goal',
    'connecting',
    'privacy',
];
/** Total steps. The position indicator renders `n/{MEMBER_REGISTRATION_STEP_COUNT}` and `MEM-001`
 * AC-1 requires the two to agree, so the indicator reads this rather than a literal. */
export const MEMBER_REGISTRATION_STEP_COUNT = MEMBER_REGISTRATION_STEP_IDS.length;
/**
 * The five steps (`MEM-001` AC-1, `MEM-002` AC-1, `MEM-003`).
 *
 * Every title and subtitle is the prototype's own string. Nothing here is written in the
 * prototype's voice as an approximation, because after 2026-08-21 there is no step the prototype
 * does not have — which was the reason the four-step version had to invent step 2's copy.
 */
export const MEMBER_REGISTRATION_STEPS = [
    {
        id: 'basics',
        position: 1,
        title: 'First, the basics',
        subtitle: "This is how we'll introduce you. Keep it human.",
        action: 'Continue',
    },
    {
        id: 'interests',
        position: 2,
        title: 'Pick 3–5 interests',
        subtitle: 'Shared interests are the best predictor of a real connection.',
        action: 'Continue',
    },
    {
        id: 'goal',
        position: 3,
        title: 'What are you working toward?',
        subtitle: 'This drives who we suggest you meet. Be specific.',
        action: 'Continue',
    },
    {
        id: 'connecting',
        position: 4,
        title: 'How do you like to connect?',
        subtitle: "We'll shape your event experience around it.",
        action: 'Continue',
    },
    {
        id: 'privacy',
        position: 5,
        title: "You're in control",
        subtitle: 'Nothing is shared unless you say so.',
        action: 'Join H2HSpark',
    },
];
export function memberRegistrationStepAt(position) {
    return MEMBER_REGISTRATION_STEPS.find((step) => step.position === position);
}
/* ============================================================================
   Field constraints
   ============================================================================ */
/**
 * The field constraints, from `MEM-002`'s and `MEM-003`'s `## Reference tables`.
 *
 * Exported as data as well as being baked into the schemas below, because the wizard renders
 * `maxLength` and a character counter from the same numbers the server refuses on.
 *
 * `city` is gone with the field itself — nothing renders it, and a limit for a control nobody
 * renders is a limit that drifts unnoticed. `conversationStarter` is back at the 120 it always
 * had: short on purpose, because it is rendered on a match card beside a name and a role, and a
 * paragraph there is a paragraph nobody reads.
 *
 * The three taxonomy selections carry no limit here. A term id is not typed by a member — it is
 * chosen from a list the platform authored — so the constraint that matters is "is this a real
 * term?", which is the taxonomy's to answer and not a character count's.
 */
export const MEMBER_REGISTRATION_FIELD_LIMITS = {
    firstName: { min: 1, max: 50 },
    lastName: { min: 1, max: 50 },
    role: { min: 1, max: 80 },
    company: { min: 1, max: 80 },
    password: { min: 8, max: 256 },
    currentGoal: { min: 0, max: 500 },
    conversationStarter: { min: 0, max: 120 },
    linkedIn: { min: 0, max: 200 },
};
/**
 * An optional taxonomy selection, as every step schema spells it.
 *
 * **No `.min(1)`, deliberately.** An empty string is what a `<select>` with an empty option
 * submits, and it has to be *accepted* here so it can be *normalised* later —
 * `partitionOptionalMatchingAnswers` turns it into an erase rather than a write. Refusing it here
 * would leave a member unable to un-choose an industry without a second, differently shaped call.
 */
const optionalTermId = () => z.string().trim().max(120).optional();
/**
 * Interest-tag cardinality for a **member** (`MEM-003` AC-5).
 *
 * Deliberately narrower than the event's 3–6 and the organizer profile's 3–8. The authoritative
 * table is `SELECTION_RULES` in the API's taxonomy domain, which every point of use reads; this
 * pair is here so the wizard can render "Pick 3–5" and disable the sixth chip without a round
 * trip.
 *
 * **Two copies of one number is exactly what `SELECTION_RULES`' own header warns about** — "three
 * copies of '3 to 5' is how one of them ends up 3 to 6" — so the copies are held together by
 * `apps/api/__tests__/domains/members/selection-rules-agree.test.ts`. The assertion lives there
 * rather than here because `contracts` must not import from an app.
 *
 * The prototype agrees in words as well as numbers: `rs1`'s heading is "Pick 3–5 interests".
 */
export const MEMBER_INTEREST_TAG_SELECTION = { min: 3, max: 5 };
/** Connection intent is multi-select with at least one (`MEM-003`'s table, and the prototype's
 * "Pick all that apply."). No upper bound. */
export const MEMBER_CONNECTION_INTENT_SELECTION = { min: 1, max: null };
/**
 * Password rule (`MEM-002`'s table): at least 8 characters, one number and one symbol.
 *
 * **The rule itself is `password-policy.ts`'s**, not a second copy. This function exists only for
 * its wording: registration shows one unmet rule at a time beside the field, where the shared
 * message names them all at once, which suits a form that submits everything together.
 *
 * It used to define a symbol as "not a letter, not a digit, not whitespace", so `£` and `—`
 * counted. That is kinder than Cognito and therefore wrong in the direction that hurts: the pool
 * accepts a published ASCII set, so a member choosing `abcdefg1£` passed here and was refused at
 * `AdminSetUserPassword` — the same failure that made an administrator's invitation link die with
 * "Something went wrong on our side". A validator may be stricter than the pool. It must never be
 * more permissive.
 */
export function describePasswordFailure(password) {
    if (describePasswordViolation(password) === null)
        return null;
    if (password.length < PASSWORD_POLICY.minimumLength) {
        return `Use at least ${String(PASSWORD_POLICY.minimumLength)} characters.`;
    }
    if (!/[0-9]/u.test(password))
        return 'Include at least one number.';
    if (EDGE_WHITESPACE.test(password))
        return PASSWORD_EDGE_SPACE_MESSAGE;
    return 'Include at least one symbol.';
}
/* ============================================================================
   Answers
   ============================================================================ */
const trimmed = (min, max) => z.string().trim().min(min).max(max);
/** Step 1, `rs0` (`MEM-002` AC-1). Email and password are the account's, not the profile's, and
 * are carried separately by {@link memberRegistrationStartSchema} — they are never stored on the
 * registration record. */
export const basicsAnswersSchema = z.object({
    firstName: trimmed(1, MEMBER_REGISTRATION_FIELD_LIMITS.firstName.max),
    lastName: trimmed(1, MEMBER_REGISTRATION_FIELD_LIMITS.lastName.max),
    role: trimmed(1, MEMBER_REGISTRATION_FIELD_LIMITS.role.max),
    company: trimmed(1, MEMBER_REGISTRATION_FIELD_LIMITS.company.max),
    /** `MEM-045` AC-1. Optional, and beside role and company because that is where the professional
     * context already is — the specification's own row is "Professional role & industry", one data
     * point in one sentence. */
    industryTermId: optionalTermId(),
    careerStageTermId: optionalTermId(),
});
/** Step 2, `rs1` (`MEM-003` AC-2, AC-5). One field, and the whole step: 3 to 5 interest tags, with
 * the live count beside them. */
export const interestsAnswersSchema = z.object({
    interestTagTermIds: z
        .array(z.string().min(1))
        .min(MEMBER_INTEREST_TAG_SELECTION.min)
        .max(MEMBER_INTEREST_TAG_SELECTION.max),
});
/**
 * Step 3, `rs2` (`MEM-003` AC-2). Two questions on one step, as the markup has them.
 *
 * **`currentGoal` is optional even though it is the step's headline.** `MEM-003`'s reference table
 * says "Current goal | No", the specification wins on behaviour, and the prototype — a wireframe
 * with the field pre-filled — says nothing either way about whether it may be left blank. So the
 * step's required content is the intent selection alone. Flagged on the pull request rather than
 * quietly promoted to required, because a heading that asks a question and accepts no answer is
 * the kind of thing a client means one way or the other.
 */
export const goalAnswersSchema = z.object({
    currentGoal: z.string().trim().max(MEMBER_REGISTRATION_FIELD_LIMITS.currentGoal.max).optional(),
    connectionIntentTermIds: z.array(z.string().min(1)).min(MEMBER_CONNECTION_INTENT_SELECTION.min),
    /** `MEM-045` AC-1. On this step because it is the same register as the goal — one free-text
     * sentence about what the member is preoccupied with — and asking both in one place is what
     * keeps it to one typing task rather than two. */
    conversationStarter: z
        .string()
        .trim()
        .max(MEMBER_REGISTRATION_FIELD_LIMITS.conversationStarter.max)
        .optional(),
});
/**
 * Step 4, `rs3` (`MEM-003` AC-3, in part).
 *
 * Format is **single-select**: the prototype's `fmtCards` compare `this.state.fmt === id` and its
 * control is a radio dot, not a checkbox. `MEM-009` AC-6 says the same thing from the other end —
 * "exactly one always selected".
 */
export const connectingAnswersSchema = z.object({
    connectionFormatTermId: z.string().min(1),
    /** `MEM-045` AC-1. This step is already the one about how a member wants to connect, and social
     * comfort is the same question asked about temperament rather than about format. Optional,
     * unlike the format beside it. */
    socialComfortTermId: optionalTermId(),
    linkedIn: z.string().trim().max(MEMBER_REGISTRATION_FIELD_LIMITS.linkedIn.max).optional(),
});
/**
 * Step 5, `rs4` (`MEM-003` AC-3, in part). The three privacy elections, on their own step because
 * `rs4` is its own step.
 *
 * Their *meaning* is `MEM-010`'s, not this story's; here they are collected. All three are
 * required booleans rather than optional ones: an election nobody made is not the same as an
 * election made `false`, and {@link MEMBER_PRIVACY_ELECTION_DEFAULTS} is what the step opens with.
 */
export const privacyAnswersSchema = z.object({
    appearInAttendeePreview: z.boolean(),
    receivePreEventMatchSuggestions: z.boolean(),
    receivePostEventFollowUpNotifications: z.boolean(),
});
/**
 * Privacy election defaults (`MEM-003`'s reference table, and the prototype's `opts` initial
 * state — `{ preview: true, match: true, intro: false }`, which agrees).
 *
 * (c) defaults off while (a) and (b) default on: appearing in a preview and receiving matches are
 * what the member came for, while post-event follow-up notifications are the one that reaches them
 * after the event is over.
 *
 * **This contradicts `apps/api/src/domains/users/README.md`**, which records the older reading
 * "preview OFF, matching ON, intros ON". `MEM-003`'s table is the current one and is what this
 * follows.
 */
export const MEMBER_PRIVACY_ELECTION_DEFAULTS = {
    appearInAttendeePreview: true,
    receivePreEventMatchSuggestions: true,
    receivePostEventFollowUpNotifications: false,
};
const STEP_SCHEMAS = {
    basics: basicsAnswersSchema,
    interests: interestsAnswersSchema,
    goal: goalAnswersSchema,
    connecting: connectingAnswersSchema,
    privacy: privacyAnswersSchema,
};
export function memberRegistrationSchemaFor(step) {
    return STEP_SCHEMAS[step];
}
/**
 * Validate one step's answers (`MEM-001` AC-2, AC-3).
 *
 * **Per step, not deferred to the end.** Returns every failing field rather than the first, so the
 * wizard can mark all of them in place; the caller focuses the first one in the returned order,
 * which is the schema's field order and therefore the visual order of the form.
 *
 * Returns an empty array when the step is satisfiable, which is also what {@link isStepComplete}
 * asks. The two are one rule with two names rather than two rules that can disagree.
 */
export function validateStep(step, answers) {
    const parsed = memberRegistrationSchemaFor(step).safeParse(answers);
    if (parsed.success)
        return [];
    return parsed.error.issues.map((issue) => ({
        field: issue.path.map(String).join('.'),
        message: messageFor(step, issue),
    }));
}
/** Member-facing wording for the failures worth wording. Zod's defaults are accurate and read like
 * a stack trace; these are the same rule said to a person. */
function messageFor(step, issue) {
    const field = issue.path[0];
    if (field === 'interestTagTermIds') {
        return `Pick ${String(MEMBER_INTEREST_TAG_SELECTION.min)} to ${String(MEMBER_INTEREST_TAG_SELECTION.max)} interests.`;
    }
    if (field === 'connectionIntentTermIds') {
        return 'Pick at least one — all that apply.';
    }
    if (issue.code === 'invalid_type' || (issue.code === 'too_small' && issue.minimum === 1)) {
        return 'This one is needed.';
    }
    if (issue.code === 'too_big' && typeof issue.maximum === 'number') {
        return `Keep this to ${String(issue.maximum)} characters.`;
    }
    void step;
    return 'Check this one.';
}
/** Is this step's required content present and within its limits? */
export function isStepComplete(step, answers) {
    return validateStep(step, answers).length === 0;
}
/* ============================================================================
   Resuming
   ============================================================================ */
/** The account's registration state. `profile_incomplete` from the moment step 1 completes
 * (`MEM-002` AC-2); `active` when the wizard completes (`MEM-001` AC-6). */
export const MEMBER_REGISTRATION_STATUSES = ['profile_incomplete', 'active'];
/**
 * Where a returning member picks up (`MEM-001` AC-5, AC-7).
 *
 * **Derived from the stored record, not from anything the browser kept.** There is no localStorage
 * draft, no session cache and no timer — the only reason a resume point exists is that a Continue
 * press wrote one, which is why the same answer arrives on a second device. A member who closes
 * the tab mid-step loses that step's uncommitted edits and nothing else, and the unsaved-changes
 * prompt is what tells them so before it happens.
 *
 * The rule is "the first step that is not complete", not "the step they were last looking at":
 * those differ when a member goes back to re-read step 2 and closes the tab, and landing them on
 * step 2 with the later steps already answered would be a wizard that lost their place.
 */
export function resumeStepFor(record) {
    if (record.status === 'active')
        return MEMBER_REGISTRATION_STEP_COUNT;
    for (const step of MEMBER_REGISTRATION_STEPS) {
        if (!isStepComplete(step.id, record.answers))
            return step.position;
    }
    return MEMBER_REGISTRATION_STEP_COUNT;
}
/** Is there anything to come back to? A record that exists at all is resumable — the account was
 * created by step 1, so its mere presence means the member gave us something. */
export function isResumable(record) {
    return record.status === 'profile_incomplete';
}
/** Every step answered — what completion requires before it may move the account to `active`
 * (`MEM-001` AC-6). Checked server-side, because a client that skipped a step is a client. */
export function isRegistrationComplete(answers) {
    return MEMBER_REGISTRATION_STEPS.every((step) => isStepComplete(step.id, answers));
}
/* ============================================================================
   Explicit saving, and the unsaved-changes prompt
   ============================================================================ */
/**
 * How this wizard saves, served as a word rather than inferred from the absence of a trigger list
 * — the same choice `EVENT_SAVE_MODE` makes, for the same reason. A client reading `explicit`
 * knows there is nothing to wire to a timer.
 */
export const MEMBER_REGISTRATION_SAVE_MODE = 'explicit';
export const MEMBER_REGISTRATION_UNSAVED_CHANGES_HEADING = 'You have unsaved changes';
/**
 * The ways out of a step holding uncommitted answers, and the prompt each raises.
 *
 * Mirrors `EVENT_UNSAVED_CHANGES_DEPARTURES` deliberately: two in-app prompts whose copy is ours,
 * and `tab-close`, which carries none because browsers render their own `beforeunload` dialog with
 * their own two buttons and no Save. What is ours in that case is the handler's *presence* — added
 * when the step first moves, removed by a successful save — so that is what a test can hold.
 *
 * Every entry prompts **only while the step is dirty**. A prompt on a clean step is itself a
 * defect: it teaches a member to dismiss dialogs, and the one that mattered goes with it.
 */
export const MEMBER_REGISTRATION_UNSAVED_CHANGES_DEPARTURES = [
    {
        departure: 'step-navigation',
        rendering: 'in-app',
        heading: MEMBER_REGISTRATION_UNSAVED_CHANGES_HEADING,
        body: "Go back now and you'll lose what you've typed on this step.",
        save: 'Save and continue',
        discard: 'Discard changes',
        cancel: 'Stay on this step',
    },
    {
        departure: 'wizard-exit',
        rendering: 'in-app',
        heading: MEMBER_REGISTRATION_UNSAVED_CHANGES_HEADING,
        body: "Leave now and you'll lose what you've typed on this step. Everything you've already saved is safe.",
        save: 'Save and leave',
        discard: 'Leave without saving',
        cancel: 'Keep going',
    },
    { departure: 'tab-close', rendering: 'browser' },
];
export function inAppDepartureFor(departure) {
    return MEMBER_REGISTRATION_UNSAVED_CHANGES_DEPARTURES.find((entry) => entry.rendering === 'in-app' && entry.departure === departure);
}
/* ============================================================================
   "Why we ask" (MEM-001 AC-4)
   ============================================================================ */
/**
 * The explainer, reachable from every step without leaving the wizard (`MEM-001` AC-4).
 *
 * The prototype puts a "Why we ask" link in step 1's subtitle only (`openWhy` on `rs0`). The story
 * requires it on **every** step, and requires it to say why each piece is collected — so it is
 * keyed by step here and the link renders in all five headers. That is a deliberate departure from
 * the prototype in the direction the story names.
 *
 * It earns its place because this wizard asks unusually personal questions. A member who
 * understands the answer is used for matching answers honestly; one who does not either skips or
 * games it, and both degrade the room.
 */
export const MEMBER_REGISTRATION_WHY_WE_ASK = {
    basics: {
        heading: 'Why we ask for this',
        body: [
            'Your name is how we introduce you — a match suggestion that said "a founder" and nothing else would be no use to either of you.',
            'Role and company are two of the few things another member sees before they decide to say hello. They are the professional context matching runs on.',
            MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK.industry,
            MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK.careerStage,
            'Your email is how your matches and follow-ups reach you. We do not ask for a phone number, and we do not store one.',
        ],
    },
    interests: {
        heading: 'Why we ask for this',
        body: [
            'Interests are the strongest single predictor of a conversation that goes somewhere, which is why we ask for three to five rather than one.',
            'Three is enough to find an overlap with somebody. More than five and the overlap stops meaning anything, because everyone shares something with everyone.',
        ],
    },
    goal: {
        heading: 'Why we ask for this',
        body: [
            'Your goal is what we match toward. Specific beats broad — "raising a first round for a community-health startup" finds people that "networking" never will.',
            'What you are seeking is how we avoid introducing two people who both wanted to be advised. It is the difference between a plausible match and a useful one.',
            MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK.conversationStarter,
        ],
    },
    connecting: {
        heading: 'Why we ask for this',
        body: [
            'How you like to connect shapes the room we put you in, not just the people. Someone who would rather have one long conversation should not be sent to work a room.',
            MEMBER_OPTIONAL_MATCHING_WHY_WE_ASK.socialComfort,
            'LinkedIn is optional. It gives the people you meet a way to find you afterwards, and it is only ever shown to a connection you accepted.',
        ],
    },
    privacy: {
        heading: 'Why we ask for this',
        body: [
            'These three choices are yours and they are reversible from My Account at any time. Nothing about you is shared with another member unless you elected it here.',
            'The middle one works in both directions: switch off match suggestions and you are never suggested to anybody else either.',
        ],
    },
};
/* ============================================================================
   The taxonomy dependency (MEM-004)
   ============================================================================ */
/**
 * The vocabularies a registration step **cannot be answered without**. A step whose required
 * vocabulary is unpopulated is blocked, which is what `MEM-004` AC-1 is about.
 *
 * Still three. `industry`, `career-stage` and `social-comfort` returned to registration on
 * 2026-08-22 but they are **not** here, and that is the distinction this constant now carries:
 * see {@link MEMBER_REGISTRATION_OPTIONAL_VOCABULARIES}.
 */
export const MEMBER_REGISTRATION_VOCABULARIES = [
    'interest-tag',
    'connection-intent',
    'connection-format',
];
/**
 * The vocabularies registration **offers** but does not require (`MEM-045` AC-2).
 *
 * An unpopulated one of these hides its control and blocks nothing. That is the whole difference
 * between the two lists, and it is not cosmetic: `MEM-004` AC-2's not-ready state exists because a
 * member cannot finish a step whose only question has no answers, and rendering that apology over
 * an *optional* field would stop a wizard for a field the member was free to ignore. An operator
 * who has not authored the `industry` vocabulary has not broken registration; they have declined
 * to offer one question.
 *
 * The failure this prevents is the quiet one in the other direction, too — an empty
 * `<select>` — and it is prevented the same way: no terms, no control.
 */
export const MEMBER_REGISTRATION_OPTIONAL_VOCABULARIES = MEMBER_OPTIONAL_MATCHING_VOCABULARIES;
/** Everything the wizard reads, required and optional together — what the vocabularies endpoint
 * fetches. Blocking is decided from {@link MEMBER_REGISTRATION_VOCABULARIES} alone. */
export const MEMBER_REGISTRATION_ALL_VOCABULARIES = [
    ...MEMBER_REGISTRATION_VOCABULARIES,
    ...MEMBER_REGISTRATION_OPTIONAL_VOCABULARIES,
];
/**
 * The vocabularies the **matching-preferences screen** selects from (`MEM-009` AC-2, AC-4, AC-5,
 * AC-6).
 *
 * A superset of registration's, and it now includes `industry` and `career-stage` as well as
 * `social-comfort`: `MEM-045` makes all three editable after registration, which is the half of
 * "optional" that matters. A field a member can only answer during the ninety seconds they are
 * signing up is not optional, it is missable.
 *
 * `MEM-009` is still the story for the screen itself. What exists today is the panel `MEM-045`
 * mounts, carrying the four optional fields and nothing else.
 */
export const MEMBER_PREFERENCES_VOCABULARIES = [
    'interest-tag',
    'connection-intent',
    'connection-format',
    'social-comfort',
    'industry',
    'career-stage',
];
/** Which step each vocabulary is needed on — so an unpopulated one is reported against the step it
 * actually blocks rather than blocking the whole wizard (`MEM-004` AC-2). */
export const MEMBER_REGISTRATION_VOCABULARY_STEPS = {
    'interest-tag': 'interests',
    'connection-intent': 'goal',
    'connection-format': 'connecting',
};
/**
 * What a member sees when a vocabulary this step needs has no terms (`MEM-004` AC-2, AC-3).
 *
 * Three constraints, all of them from the story and all of them easy to break by writing the
 * obvious thing instead:
 *
 *   - **Ordinary member-facing language.** "The taxonomy is unpopulated" is operator vocabulary. A
 *     member is not the audience for the platform's configuration state, and `ADM-004`'s
 *     resolution of 2026-08-17 says the member-facing wording does not expose it.
 *   - **Not styled as an error.** Nothing has gone wrong that the member did or can fix.
 *   - **Not an empty control.** A select with no options is the failure this replaces.
 *
 * And the member is not blamed, and is told what they can do next.
 */
export const MEMBER_REGISTRATION_VOCABULARY_UNAVAILABLE = {
    heading: 'This part is not ready yet',
    body: "We're still setting up the choices for this step, so there's nothing here to pick from yet. Nothing you've entered so far is lost.",
    nextStep: "Your account is saved. Come back a little later and you'll carry on from right here — we'll email you when it's ready.",
};
/* ============================================================================
   Wire shapes
   ============================================================================ */
/** Step 1's request (`MEM-002`). The one call that creates an account, so the only one whose caller
 * has no session. */
export const memberRegistrationStartSchema = basicsAnswersSchema.extend({
    /** Lower-cased as it is parsed (`USR-015` AC-1): this door used to trim only, and its rows were
     * the ones every other lookup missed. */
    email: emailAddressField,
    /** Absent on the Google path, where the identity provider carries the credential. `MEM-002`
     * AC-3: Google shortens step 1, it does not skip it — the basics fields above are still
     * required, because Google supplies a name and an email and never a role or a company. */
    password: z.string().min(1).max(MEMBER_REGISTRATION_FIELD_LIMITS.password.max).optional(),
    /** An opaque token from the social exchange, present exactly when `password` is absent. */
    googleInitCode: z.string().min(1).optional(),
    /** Where completion should return the member (`MEM-001` AC-6). A path within the app, never an
     * absolute URL — see `isSafeReturnTo`. */
    returnTo: z.string().max(512).optional(),
});
/**
 * Step 1's request for somebody who is **already signed in** (`MEM-002` AC-7, `MEM-001` AC-7).
 *
 * ## Why this is a second schema rather than three optional fields on the first
 *
 * An organizer — or anybody else holding an account — who opens `/app/join` is not creating an
 * account, they are becoming a member on the account they have. So this shape has **no `email`,
 * no `password` and no `googleInitCode`**, and its route reads the member's own id from the
 * session like every other authenticated route in the domain.
 *
 * That absence is the security property, not a convenience. `MEM-002` AC-6 forbids step 1 from
 * revealing whether an address already has an account; a request that names no address cannot
 * answer a question about one, whatever its handler does next. Widening
 * {@link memberRegistrationStartSchema} to make `password` conditionally optional would instead
 * have produced one route whose answer depends on whether a session was attached — which is
 * precisely the branch AC-6 exists to keep out of that response.
 *
 * The basics themselves are still required, for `MEM-002` AC-3's reason applied to a different
 * door: an existing account carries a name and an address and carries neither a role nor a
 * company, and those two are most of what another member sees on a preview card.
 */
export const memberRegistrationStartForAccountSchema = basicsAnswersSchema.extend({
    /** Where completion should return the member (`MEM-001` AC-6). Same rule as above. */
    returnTo: z.string().max(512).optional(),
});
/** An explicit save of one step (`MEM-001` AC-2, and the explicit-saving rule). */
export const memberRegistrationSaveSchema = z.object({
    step: z.enum(MEMBER_REGISTRATION_STEP_IDS),
    answers: z.record(z.unknown()),
});
/**
 * Is this a safe place to send a member after completion (`MEM-001` AC-6)?
 *
 * `returnTo` arrives from the query string of whatever link brought them to registration, so it is
 * attacker-controlled: an absolute URL here is an open redirect, and `//evil.example` is an
 * absolute URL that looks relative. Same-origin app paths only, and backslashes are rejected
 * because some browsers normalise them to slashes.
 */
export function isSafeReturnTo(returnTo) {
    if (returnTo === undefined || returnTo.length === 0)
        return false;
    if (!returnTo.startsWith('/'))
        return false;
    if (returnTo.startsWith('//'))
        return false;
    if (returnTo.includes('\\'))
        return false;
    return true;
}
/** Where a member goes when they finish and there is nowhere in particular to send them back to. */
export const MEMBER_REGISTRATION_DEFAULT_RETURN_TO = '/app';
export function resolveReturnTo(returnTo) {
    return isSafeReturnTo(returnTo) ? returnTo : MEMBER_REGISTRATION_DEFAULT_RETURN_TO;
}
//# sourceMappingURL=member-registration.js.map